import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Sparkles, Trash2, Link2 } from 'lucide-react';

import { Card } from '../../../components/ui/Card.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { MultiSelect } from '../../../components/ui/MultiSelect.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { GalleryManager } from '../../../components/ui/GalleryManager.jsx';
import { productsApi } from '../../../api/products.js';
import { categoriesApi } from '../../../api/categories.js';
import { brandsApi } from '../../../api/brands.js';
import { storesApi } from '../../../api/stores.js';
import { toolsApi } from '../../../api/tools.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

import { StructuredReadingEditor } from './StructuredReadingEditor.jsx';
import { ProductVariantsSection } from './ProductVariantsSection.jsx';
import { AttributesEditor } from './AttributesEditor.jsx';
import { RECIPE_SECTIONS, PLATE_SECTIONS, readingToMap, mapToReading } from './ormeSections.js';
import { InheritStatus } from './variantInheritance.jsx';
import { baseHas, isEmptyStr, isEmptyArr, isEmptyRange, isEmptyNutrition, isEmptyReadingMap } from './variantInheritance.js';

// Declared quantity units, mirroring Product.INGREDIENT_UNITS on the server.
const INGREDIENT_UNITS = ['%', 'g/kg', 'mg/kg', 'UI/kg', 'g', 'mg'];
// Units for the analytical constituents and additives, mirroring
// Product.ANALYTICAL_UNITS / Product.ADDITIVE_UNITS on the server.
const ANALYTICAL_UNITS = ['%', 'g/kg', 'mg/kg', 'kcal/kg'];
const ADDITIVE_UNITS = ['UI/kg', 'mg/kg', 'g/kg', 'UI', 'mg', 'g', '%'];
const ADDITIVE_CATEGORIES = [
  { value: 'vitamin', label: 'Vitamina' },
  { value: 'mineral', label: 'Minerale' },
  { value: 'trace', label: 'Oligoelemento' },
  { value: 'zootechnical', label: 'Zootecnico' },
  { value: 'other', label: 'Altro' }
];

// Map common analytical-constituent names to the fixed nutritionalProfile fields
// the Plate math reads, so an extracted block back-fills the empty fixed fields.
const NUTRITION_BACKFILL = [
  { field: 'npProtein', keys: ['proteina grezza', 'proteine grezze', 'proteina', 'proteine'] },
  { field: 'npFat', keys: ['oli e grassi grezzi', 'grassi grezzi', 'materie grasse', 'tenore di grassi', 'grassi'] },
  { field: 'npFiber', keys: ['fibra grezza', 'fibre grezze', 'fibra', 'fibre'] },
  { field: 'npAsh', keys: ['ceneri grezze', 'ceneri'] },
  { field: 'npMoisture', keys: ['umidità', 'umidita'] }
];

function num(v) {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function readingFromMap(defs, map) {
  const has = defs.some((d) => (map[d.key] || '').trim());
  return has ? mapToReading(defs, map) : null;
}

function pickKnown(defs, obj) {
  const out = {};
  defs.forEach((d) => { if (obj && typeof obj[d.key] === 'string') out[d.key] = obj[d.key]; });
  return out;
}

function stripUrls(media) {
  return (media || []).map((m) => { const copy = { ...m }; delete copy.url; return copy; });
}

function toDefaults(p) {
  if (!p) return {
    sku: '', name: '', brandId: '', description: '',
    speciesIds: [], breedIds: [], categoryIds: [],
    ageMin: '', ageMax: '', weightMin: '', weightMax: '',
    priceAmount: '', priceTaxIncluded: 'true', storeIds: [],
    ingredients: [], ingredientLines: [],
    analyticalConstituents: [], additives: [],
    npProtein: '', npFat: '', npFiber: '', npAsh: '', npMoisture: '', npKcal: '', npLifestage: '',
    tags: [], status: 'active'
  };
  return {
    sku: p.sku || '',
    name: p.name || '',
    brandId: String(p.brandId?._id || p.brandId || ''),
    description: p.description || '',
    speciesIds: (p.speciesIds || []).map((x) => x._id || x),
    breedIds: (p.breedIds || []).map((x) => x._id || x),
    categoryIds: (p.categoryIds || []).map((x) => x._id || x),
    ageMin: p.ageRangeMonths?.min ?? '',
    ageMax: p.ageRangeMonths?.max ?? '',
    weightMin: p.weightRangeKg?.min ?? '',
    weightMax: p.weightRangeKg?.max ?? '',
    priceAmount: p.price?.amount ?? '',
    priceTaxIncluded: String(p.price?.taxIncluded !== false),
    storeIds: (p.storeIds || []).map((x) => x._id || x),
    ingredients: p.ingredients || [],
    ingredientLines: (p.ingredientLines || []).map((l) => ({
      name: l.name || '', percent: l.percent ?? '', amount: l.amount ?? '', unit: l.unit || (l.percent != null ? '%' : ''), function: l.function || '', allergen: Boolean(l.allergen), origin: l.origin || ''
    })),
    analyticalConstituents: (p.analyticalConstituents || []).map((c) => ({
      name: c.name || '', amount: c.amount ?? '', unit: c.unit || ''
    })),
    additives: (p.additives || []).map((a) => ({
      name: a.name || '', amount: a.amount ?? '', unit: a.unit || '', category: a.category || ''
    })),
    npProtein: p.nutritionalProfile?.proteinPct ?? '',
    npFat: p.nutritionalProfile?.fatPct ?? '',
    npFiber: p.nutritionalProfile?.fiberPct ?? '',
    npAsh: p.nutritionalProfile?.ashPct ?? '',
    npMoisture: p.nutritionalProfile?.moisturePct ?? '',
    npKcal: p.nutritionalProfile?.kcalPerKg ?? '',
    npLifestage: p.nutritionalProfile?.lifestage || '',
    ormeLevel: p.ormeLevel ? String(p.ormeLevel) : '',
    tags: p.tags || [],
    status: p.status || 'active'
  };
}

function SectionTitle({ title, hint, action }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h3 className="text-base font-semibold text-ink">{title}</h3>
        {hint && <p className="text-xs text-ink-muted">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

/**
 * Product create/edit form rendered as a page, organised in cards. Seeded once
 * from `product` (mounted with the data already loaded). Handles the core
 * fields, the gallery, the ORME readings, variants and (for a variant) the
 * inheritance banner, free attributes and base-product link.
 */
export function ProductForm({ product, editing }) {
  const isVariant = Boolean(product && product.parentId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [inventory, setInventory] = useState(() => {
    const inv = {};
    for (const row of product?.inventory || []) {
      inv[String(row.storeId?._id || row.storeId)] = { quantity: row.quantity ?? 0, reorderPoint: row.reorderPoint };
    }
    return inv;
  });
  const [recipeMap, setRecipeMap] = useState(() => readingToMap(RECIPE_SECTIONS, product?.recipeReading));
  const [plateMap, setPlateMap] = useState(() => readingToMap(PLATE_SECTIONS, product?.plateReading));
  const [attrs, setAttrs] = useState(() => product?.variantAttributes || {});
  const [media, setMedia] = useState(() => product?.media || []);

  const speciesQuery = useQuery({ queryKey: ['cat', 'species'], queryFn: () => categoriesApi.list({ kind: 'species', limit: 100, active: true }) });
  const breedQuery = useQuery({ queryKey: ['cat', 'breed'], queryFn: () => categoriesApi.list({ kind: 'breed', limit: 100, active: true }) });
  const productCatQuery = useQuery({ queryKey: ['cat', 'productCategory'], queryFn: () => categoriesApi.list({ kind: 'productCategory', limit: 100, active: true }) });
  const storesQuery = useQuery({ queryKey: ['stores', 'all'], queryFn: () => storesApi.list({ limit: 100 }) });
  const brandsQuery = useQuery({ queryKey: ['brands', 'all'], queryFn: () => brandsApi.list({ limit: 100, active: true }) });
  const brandOpts = useMemo(() => ((brandsQuery.data && brandsQuery.data.items) || []), [brandsQuery.data]);

  const speciesOpts = useMemo(() => ((speciesQuery.data && speciesQuery.data.items) || []).map((c) => ({ value: c._id, label: c.name })), [speciesQuery.data]);
  const breedOpts = useMemo(() => ((breedQuery.data && breedQuery.data.items) || []).map((c) => ({ value: c._id, label: c.name })), [breedQuery.data]);
  const productCatOpts = useMemo(() => ((productCatQuery.data && productCatQuery.data.items) || []).map((c) => ({ value: c._id, label: c.name })), [productCatQuery.data]);
  const storeOpts = useMemo(() => ((storesQuery.data && storesQuery.data.items) || []).map((s) => ({ value: s._id, label: s.name, hint: s.code })), [storesQuery.data]);
  const storeMap = useMemo(() => new Map(((storesQuery.data && storesQuery.data.items) || []).map((s) => [String(s._id), s])), [storesQuery.data]);

  const { register, handleSubmit, watch, control, setError, setValue, getValues, formState: { errors } } = useForm({ defaultValues: toDefaults(product) });
  const watchedStoreIds = watch('storeIds');
  const { fields: lineFields, append: appendLine, remove: removeLine, replace: replaceLines } = useFieldArray({ control, name: 'ingredientLines' });
  const { fields: acFields, append: appendAc, remove: removeAc, replace: replaceAc } = useFieldArray({ control, name: 'analyticalConstituents' });
  const { fields: addFields, append: appendAdd, remove: removeAdd, replace: replaceAdd } = useFieldArray({ control, name: 'additives' });
  const watchedLines = watch('ingredientLines');
  const watchedIngredients = watch('ingredients');
  const [declaration, setDeclaration] = useState('');
  const [labelText, setLabelText] = useState('');

  // Variant inheritance: the base product's own inheritable values arrive on
  // `product.base`. We watch the live form so each field can show, in real time,
  // whether it is still inherited from the base or has been overridden, and
  // preview the inherited content next to an empty field.
  const base = product?.base || {};
  const wAll = watch();
  const labelsFrom = (ids, opts) => {
    const m = new Map(opts.map((o) => [String(o.value), o.label]));
    return (ids || []).map((x) => m.get(String(x?._id || x))).filter(Boolean).join(', ');
  };
  const rangeText = (r, unit) => {
    if (!r) return '';
    const lo = r.min ?? null;
    const hi = r.max ?? null;
    if (lo === null && hi === null) return '';
    return `${lo === null ? '...' : lo} - ${hi === null ? '...' : hi} ${unit}`;
  };
  const npText = (n) => {
    if (!n) return '';
    const parts = [];
    if (n.proteinPct != null) parts.push(`Prot ${n.proteinPct}%`);
    if (n.fatPct != null) parts.push(`Grassi ${n.fatPct}%`);
    if (n.fiberPct != null) parts.push(`Fibre ${n.fiberPct}%`);
    if (n.ashPct != null) parts.push(`Ceneri ${n.ashPct}%`);
    if (n.moisturePct != null) parts.push(`Umidità ${n.moisturePct}%`);
    if (n.kcalPerKg != null) parts.push(`${n.kcalPerKg} kcal/kg`);
    return parts.join(' · ');
  };
  const ingText = (lines) => {
    const l = (lines || []).filter((x) => x && x.name && String(x.name).trim());
    if (!l.length) return '';
    const names = l.slice(0, 5).map((x) => x.name.trim()).join(', ');
    return `${l.length} ingredienti: ${names}${l.length > 5 ? '...' : ''}`;
  };
  const readingText = (r) => {
    const sections = r && Array.isArray(r.sections) ? r.sections : [];
    const n = sections.filter((s) => s && s.body && s.body.text && String(s.body.text).trim()).length;
    return n ? `${n} sezioni compilate` : '';
  };
  const liveLines = (wAll.ingredientLines || []).filter((l) => l && l.name && String(l.name).trim());
  const liveAc = (wAll.analyticalConstituents || []).filter((c) => c && c.name && String(c.name).trim());
  const liveAdd = (wAll.additives || []).filter((a) => a && a.name && String(a.name).trim());
  const acText = (list) => (list || [])
    .filter((c) => c && c.name)
    .map((c) => (c.amount != null && c.amount !== '' ? `${c.name} ${c.amount}${c.unit && c.unit !== '%' ? ` ${c.unit}` : '%'}` : c.name))
    .slice(0, 8).join(', ');
  const addText = (list) => {
    const l = (list || []).filter((a) => a && a.name);
    if (!l.length) return '';
    const head = l.slice(0, 5).map((a) => a.name).join(', ');
    return `${l.length} additivi: ${head}${l.length > 5 ? '...' : ''}`;
  };

  // Map a structured line (from either the AI tool or the regex parser) into the
  // form's editable shape.
  const toFormLine = (l) => {
    const amount = l.amount ?? (l.percent ?? '');
    const unit = l.unit || (l.percent != null ? '%' : '');
    return {
      name: l.name || '',
      amount: amount === null ? '' : amount,
      unit,
      percent: unit === '%' ? (amount === null ? '' : amount) : '',
      function: l.function || '',
      allergen: !!l.allergen,
      origin: l.origin || ''
    };
  };

  // Quick entry: extract structured ingredients from a pasted declaration. The
  // AI tool (ai.tools.ingredientExtract) fills function and allergen too; if it
  // is disabled or fails, fall back to the deterministic, no-cost regex parser.
  // The structured lines are the single source of truth (the flat ingredients[]
  // is regenerated from them server-side on save).
  const parseIngredients = useMutation({
    mutationFn: async (text) => {
      try {
        const res = await toolsApi.extractIngredients(text);
        const lines = (res?.data?.ingredients || []).map(toFormLine);
        if (lines.length) return { lines, source: 'ai' };
      } catch (err) {
        // 403 = tool disabled; anything else = transient model/parse error.
        if (!(err instanceof ApiError)) throw err;
      }
      const res = await productsApi.parseIngredients(text);
      return { lines: (res.ingredientLines || []).map(toFormLine), source: 'parser' };
    },
    onSuccess: ({ lines, source }) => {
      if (!lines.length) { toast.error('Nessun ingrediente riconosciuto.'); return; }
      replaceLines(lines);
      setDeclaration('');
      toast.success(`${lines.length} ingredienti estratti${source === 'ai' ? ' con AI' : ''}. Rivedi e salva.`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  // Map an extracted constituent/additive (from the AI tool or the parser) into
  // the form's editable row shape.
  const toAcForm = (c) => ({ name: c.name || '', amount: c.amount ?? '', unit: c.unit || '' });
  const toAddForm = (a) => ({ name: a.name || '', amount: a.amount ?? '', unit: a.unit || '', category: a.category || '' });

  // Back-fill the empty fixed nutritionalProfile fields from the matching
  // analytical constituents, so the Plate math keeps working without retyping.
  // Never overwrites a value the operator already entered.
  const backfillNutrition = (acRows) => {
    const find = (keys) => acRows.find((c) => {
      const n = String(c.name || '').toLowerCase().trim();
      return keys.some((k) => n === k) || keys.some((k) => n.includes(k));
    });
    for (const { field, keys } of NUTRITION_BACKFILL) {
      const hit = find(keys);
      if (hit && hit.amount !== '' && hit.amount != null) {
        const cur = getValues(field);
        if (cur === '' || cur === null || cur === undefined) setValue(field, hit.amount, { shouldDirty: true });
      }
    }
    const kcal = acRows.find((c) => /kcal|energia/.test(String(c.name || '').toLowerCase()));
    if (kcal && kcal.amount !== '' && kcal.amount != null) {
      const cur = getValues('npKcal');
      if (cur === '' || cur === null || cur === undefined) setValue('npKcal', kcal.amount, { shouldDirty: true });
    }
  };

  // Quick entry: paste the full label block (analytical constituents + additives)
  // and extract both structured lists in one step, mirroring the ingredient
  // quick-entry. The AI tool fills the additive category; if it is disabled or
  // fails, fall back to the deterministic, no-cost parser.
  const parseLabel = useMutation({
    mutationFn: async (text) => {
      try {
        const res = await toolsApi.extractLabel(text);
        const d = res?.data || {};
        if ((d.analyticalConstituents || []).length || (d.additives || []).length) {
          return { ac: (d.analyticalConstituents || []).map(toAcForm), add: (d.additives || []).map(toAddForm), source: 'ai' };
        }
      } catch (err) {
        if (!(err instanceof ApiError)) throw err;
      }
      const res = await productsApi.parseLabel(text);
      return { ac: (res.analyticalConstituents || []).map(toAcForm), add: (res.additives || []).map(toAddForm), source: 'parser' };
    },
    onSuccess: ({ ac, add, source }) => {
      if (!ac.length && !add.length) { toast.error('Nessun dato riconosciuto nell\'etichetta.'); return; }
      if (ac.length) { replaceAc(ac); backfillNutrition(ac); }
      if (add.length) replaceAdd(add);
      setLabelText('');
      toast.success(`Etichetta interpretata${source === 'ai' ? ' con AI' : ''}: ${ac.length} componenti, ${add.length} additivi. Rivedi e salva.`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  // Read-only preview of the canonical ingredient list: derived from the
  // structured lines, falling back to the legacy flat list when none exist.
  const ingredientPreview = useMemo(() => {
    const lines = (watchedLines || []).filter((l) => l && l.name && String(l.name).trim());
    if (lines.length) {
      return lines.map((l) => {
        const name = l.name.trim();
        if (l.amount === '' || l.amount == null) return name;
        const unit = l.unit && l.unit !== '%' ? ` ${l.unit}` : '%';
        return `${name} ${l.amount}${unit}`;
      });
    }
    return watchedIngredients || [];
  }, [watchedLines, watchedIngredients]);

  useEffect(() => {
    const allowed = new Set((watchedStoreIds || []).map(String));
    setInventory((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) if (allowed.has(k)) next[k] = v;
      return next;
    });
  }, [watchedStoreIds]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const inventoryArr = (values.storeIds || []).map((sid) => {
        const row = inventory[String(sid)];
        if (!row) return null;
        const item = { storeId: sid, quantity: Number(row.quantity) || 0 };
        if (row.reorderPoint !== '' && row.reorderPoint !== undefined && row.reorderPoint !== null) item.reorderPoint = Number(row.reorderPoint);
        return item;
      }).filter(Boolean);

      const payload = {
        sku: values.sku.toUpperCase(),
        name: values.name,
        brandId: values.brandId || null,
        description: values.description || undefined,
        speciesIds: values.speciesIds,
        breedIds: values.breedIds,
        categoryIds: values.categoryIds,
        ageRangeMonths: { min: num(values.ageMin), max: num(values.ageMax) },
        weightRangeKg: { min: num(values.weightMin), max: num(values.weightMax) },
        ingredients: values.ingredients,
        ingredientLines: (values.ingredientLines || []).filter((l) => l.name && l.name.trim()).map((l) => {
          const line = { name: l.name.trim(), allergen: Boolean(l.allergen) };
          const amount = num(l.amount);
          if (amount !== undefined) line.amount = amount;
          if (l.unit) line.unit = l.unit;
          // Keep percent in sync for the Plate math when the unit is a percentage.
          const pct = num(l.percent);
          if (l.unit === '%' && amount !== undefined) line.percent = amount;
          else if (pct !== undefined) line.percent = pct;
          if (l.function && l.function.trim()) line.function = l.function.trim();
          if (l.origin && l.origin.trim()) line.origin = l.origin.trim();
          return line;
        }),
        analyticalConstituents: (values.analyticalConstituents || [])
          .filter((c) => c.name && c.name.trim())
          .map((c) => {
            const row = { name: c.name.trim() };
            const amount = num(c.amount);
            if (amount !== undefined) row.amount = amount;
            if (c.unit) row.unit = c.unit;
            return row;
          }),
        additives: (values.additives || [])
          .filter((a) => a.name && a.name.trim())
          .map((a) => {
            const row = { name: a.name.trim() };
            const amount = num(a.amount);
            if (amount !== undefined) row.amount = amount;
            if (a.unit) row.unit = a.unit;
            if (a.category) row.category = a.category;
            return row;
          }),
        nutritionalProfile: {
          proteinPct: num(values.npProtein) ?? null,
          fatPct: num(values.npFat) ?? null,
          fiberPct: num(values.npFiber) ?? null,
          ashPct: num(values.npAsh) ?? null,
          moisturePct: num(values.npMoisture) ?? null,
          kcalPerKg: num(values.npKcal) ?? null,
          lifestage: values.npLifestage || null
        },
        ormeLevel: values.ormeLevel ? Number(values.ormeLevel) : null,
        tags: values.tags,
        price: { amount: Number(values.priceAmount), currency: 'EUR', taxIncluded: values.priceTaxIncluded === 'true' },
        storeIds: values.storeIds,
        inventory: inventoryArr,
        status: values.status,
        recipeReading: readingFromMap(RECIPE_SECTIONS, recipeMap),
        plateReading: readingFromMap(PLATE_SECTIONS, plateMap)
      };
      payload.variantAttributes = attrs;

      if (!payload.ageRangeMonths.min && payload.ageRangeMonths.min !== 0 && !payload.ageRangeMonths.max && payload.ageRangeMonths.max !== 0) delete payload.ageRangeMonths;
      if (!payload.weightRangeKg.min && payload.weightRangeKg.min !== 0 && !payload.weightRangeKg.max && payload.weightRangeKg.max !== 0) delete payload.weightRangeKg;
      for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];

      return editing ? productsApi.update(product._id, payload) : productsApi.create(payload);
    },
    onSuccess: (saved) => {
      toast.success(editing ? 'Prodotto aggiornato.' : 'Prodotto creato.');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Stay on the edit page after saving. On create, switch to the new
      // product's edit URL (so the gallery and variants become available)
      // without leaving the page.
      if (!editing && saved?._id) navigate(`/admin/products/${saved._id}/edit`, { replace: true });
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  const uploadImg = useMutation({
    mutationFn: (file) => productsApi.uploadMedia(product._id, file),
    onSuccess: (p) => { setMedia(p.media || []); queryClient.invalidateQueries({ queryKey: ['products'] }); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const deleteImg = useMutation({
    mutationFn: (key) => productsApi.deleteMedia(product._id, key),
    onSuccess: (p) => { setMedia(p.media || []); queryClient.invalidateQueries({ queryKey: ['products'] }); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const patchMedia = useMutation({
    mutationFn: (next) => productsApi.update(product._id, { media: stripUrls(next) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['products'] }),
    onError: (err) => toast.error(err.message || t('common.error'))
  });
  const replaceMedia = (next) => { setMedia(next); patchMedia.mutate(next); };

  const generate = useMutation({
    mutationFn: () => {
      const vals = watch();
      const lines = (vals.ingredientLines || []).filter((l) => l.name && l.name.trim())
        .map((l) => ({ name: l.name.trim(), percent: num(l.percent), function: l.function || undefined, allergen: Boolean(l.allergen), origin: l.origin || undefined }));
      return toolsApi.recipeReading({
        name: vals.name || undefined,
        proteinSource: attrs.gusto || attrs.fonteProteica || undefined,
        ingredients: (vals.ingredients || []).length ? vals.ingredients : undefined,
        ingredientLines: lines.length ? lines : undefined,
        nutritionalProfile: {
          proteinPct: num(vals.npProtein) ?? null, fatPct: num(vals.npFat) ?? null, fiberPct: num(vals.npFiber) ?? null,
          ashPct: num(vals.npAsh) ?? null, moisturePct: num(vals.npMoisture) ?? null, kcalPerKg: num(vals.npKcal) ?? null, lifestage: vals.npLifestage || null
        }
      });
    },
    onSuccess: (res) => {
      setRecipeMap((prev) => ({ ...prev, ...pickKnown(RECIPE_SECTIONS, res?.data?.recipeReading) }));
      setPlateMap((prev) => ({ ...prev, ...pickKnown(PLATE_SECTIONS, res?.data?.plateReading) }));
      toast.success(`Bozza generata${res?.model ? ` (${res.model})` : ''}. Rivedi e salva.`);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });
  const onGenerate = () => {
    const hasContent = Object.values(recipeMap).some(Boolean) || Object.values(plateMap).some(Boolean);
    if (hasContent && !window.confirm('Sovrascrivere le letture ORME con quelle generate dall\'AI?')) return;
    generate.mutate();
  };

  const detach = async () => {
    if (!window.confirm('Scollegare la variante dal prodotto base? Manterrà i valori attualmente ereditati.')) return;
    try { await productsApi.detachVariant(product._id); toast.success('Variante scollegata.'); navigate('/admin/products'); }
    catch (err) { toast.error(err.message || t('common.error')); }
  };

  const updateInventoryQty = (sid, value) => setInventory((prev) => ({ ...prev, [String(sid)]: { ...prev[String(sid)], quantity: value } }));
  const updateReorder = (sid, value) => setInventory((prev) => ({ ...prev, [String(sid)]: { ...prev[String(sid)], reorderPoint: value } }));

  return (
    <form id="product-form" className="flex flex-col gap-5 pb-24" onSubmit={handleSubmit(mutation.mutate)} noValidate>
      {isVariant && (
        <Card className="border-accent/40 bg-accent/5">
          <SectionTitle
            title="Variante"
            hint='I campi vuoti ereditano dal prodotto base (badge "Ereditato dal base", con anteprima del contenuto). Compilane uno solo per sovrascriverlo: diventa "Modificato sulla variante".'
            action={
              <Button type="button" variant="ghost" size="sm" onClick={detach}>
                <Link2 className="mr-1 h-3.5 w-3.5" /> Scollega dal base
              </Button>
            }
          />
          {product.parent && (
            <p className="mb-4 text-sm text-ink-muted">
              Base: <Link to={`/admin/products/${product.parent._id}/edit`} className="text-accent hover:underline">{product.parent.name}</Link>{' '}
              <code className="rounded bg-border/40 px-1 py-0.5 text-xs">{product.parent.sku}</code>
            </p>
          )}
          <AttributesEditor value={attrs} onChange={setAttrs} />
        </Card>
      )}

      {!isVariant && (
        <Card>
          <SectionTitle title="Attributi" hint="Se questo prodotto ha varianti, qui imposti i suoi attributi come formato base (es. Peso = 800gr)." />
          <AttributesEditor value={attrs} onChange={setAttrs} />
        </Card>
      )}

      <Card>
        <SectionTitle title="Informazioni" hint="Dati principali del prodotto." />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="SKU" hint="maiuscolo, cifre, - o _ (2-64)" error={errors.sku?.message} disabled={editing}
            {...register('sku', editing ? {} : { required: 'SKU richiesto', pattern: { value: /^[A-Z0-9_-]{2,64}$/i, message: 'formato non valido' } })} />
          <Input label="Nome" error={errors.name?.message} {...register('name', { required: 'Nome richiesto' })} />
          <Controller
            control={control}
            name="brandId"
            render={({ field }) => (
              <div>
                <Select label="Brand" error={errors.brandId?.message} value={field.value || ''} onChange={field.onChange}>
                  <option value="">Nessuno</option>
                  {brandOpts.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                </Select>
                {isVariant && <InheritStatus show={baseHas(base, 'brand')} empty={isEmptyStr(field.value)} preview={base.brand} />}
              </div>
            )}
          />
          <Select label="Stato" error={errors.status?.message} {...register('status')}>
            <option value="active">Attivo</option>
            <option value="draft">Bozza</option>
            <option value="archived">Archiviato</option>
          </Select>
          <div className="sm:col-span-2">
            <Textarea label="Descrizione" rows={4} error={errors.description?.message} {...register('description')} />
            {isVariant && <InheritStatus show={baseHas(base, 'description')} empty={isEmptyStr(wAll.description)} preview={base.description} />}
          </div>
        </div>
      </Card>

      <Card>
        <SectionTitle title="Immagini" hint="La principale è la copertina mostrata in catalogo." />
        {editing ? (
          <GalleryManager
            media={media}
            onUpload={(file) => uploadImg.mutateAsync(file)}
            onDelete={(key) => deleteImg.mutate(key)}
            onReplace={replaceMedia}
            busy={uploadImg.isPending || deleteImg.isPending || patchMedia.isPending}
          />
        ) : (
          <p className="text-sm text-ink-faint">Salva il prodotto per caricare le immagini.</p>
        )}
      </Card>

      <Card>
        <SectionTitle title="Categorie e target" hint="Specie, razze, categorie e fascia di età/peso a cui è destinato." />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Controller control={control} name="speciesIds" render={({ field }) => (
            <div>
              <MultiSelect label="Specie" options={speciesOpts} value={field.value} onChange={field.onChange} />
              {isVariant && <InheritStatus show={baseHas(base, 'speciesIds')} empty={isEmptyArr(field.value)} preview={labelsFrom(base.speciesIds, speciesOpts)} />}
            </div>
          )} />
          <Controller control={control} name="breedIds" render={({ field }) => (
            <div>
              <MultiSelect label="Razze" options={breedOpts} value={field.value} onChange={field.onChange} />
              {isVariant && <InheritStatus show={baseHas(base, 'breedIds')} empty={isEmptyArr(field.value)} preview={labelsFrom(base.breedIds, breedOpts)} />}
            </div>
          )} />
          <Controller control={control} name="categoryIds" render={({ field }) => (
            <div>
              <MultiSelect label="Categorie prodotto" options={productCatOpts} value={field.value} onChange={field.onChange} />
              {isVariant && <InheritStatus show={baseHas(base, 'categoryIds')} empty={isEmptyArr(field.value)} preview={labelsFrom(base.categoryIds, productCatOpts)} />}
            </div>
          )} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Input type="number" label="Età min (mesi)" min={0} {...register('ageMin')} />
          <Input type="number" label="Età max (mesi)" min={0} {...register('ageMax')} />
          <Input type="number" label="Peso min (kg)" step="0.1" min={0} {...register('weightMin')} />
          <Input type="number" label="Peso max (kg)" step="0.1" min={0} {...register('weightMax')} />
        </div>
        {isVariant && (
          <div className="mt-2 flex flex-col gap-1">
            <InheritStatus show={baseHas(base, 'ageRangeMonths')} empty={isEmptyRange(wAll.ageMin, wAll.ageMax)} preview={`Età: ${rangeText(base.ageRangeMonths, 'mesi')}`} />
            <InheritStatus show={baseHas(base, 'weightRangeKg')} empty={isEmptyRange(wAll.weightMin, wAll.weightMax)} preview={`Peso: ${rangeText(base.weightRangeKg, 'kg')}`} />
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle title="Prezzo e disponibilità" hint="Prezzo di vendita e scorte per negozio." />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Input type="number" label="Prezzo (EUR)" min={0} step="0.01" error={errors.priceAmount?.message} {...register('priceAmount', { required: 'Prezzo richiesto', min: 0 })} />
          <Select label="IVA inclusa" {...register('priceTaxIncluded')}>
            <option value="true">Sì</option>
            <option value="false">No</option>
          </Select>
        </div>
        <div className="mt-4">
          <Controller control={control} name="storeIds" rules={{ validate: (v) => (v && v.length > 0) || 'Selezionare almeno un negozio' }}
            render={({ field }) => <MultiSelect label="Negozi che vendono il prodotto" options={storeOpts} value={field.value} onChange={field.onChange} error={errors.storeIds?.message} />} />
          {(watchedStoreIds || []).length > 0 && (
            <div className="mt-3 overflow-hidden rounded-lg border border-border">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-surface">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Negozio</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Quantità</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Punto di riordino</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {watchedStoreIds.map((sid) => {
                    const s = storeMap.get(String(sid));
                    const row = inventory[String(sid)] || { quantity: '', reorderPoint: '' };
                    return (
                      <tr key={String(sid)}>
                        <td className="px-3 py-2 text-sm">{s ? `${s.name} (${s.code})` : String(sid)}</td>
                        <td className="px-3 py-2"><input type="number" min={0} value={row.quantity ?? ''} onChange={(e) => updateInventoryQty(sid, e.target.value)} className="h-8 w-24 rounded-md border border-border bg-surface-elevated px-2 text-sm focus:outline-none" /></td>
                        <td className="px-3 py-2"><input type="number" min={0} value={row.reorderPoint ?? ''} onChange={(e) => updateReorder(sid, e.target.value)} className="h-8 w-24 rounded-md border border-border bg-surface-elevated px-2 text-sm focus:outline-none" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <SectionTitle title="Composizione" hint={isVariant ? 'Vuoto = eredita dal base.' : 'Ingredienti, profilo nutrizionale e composizione dettagliata.'} />

        {/* Quick entry: paste the label declaration and extract the structured
            ingredients in one step. The structured lines below are the source
            of truth; the flat ingredient list is derived from them on save. */}
        <div className="rounded-md border border-border bg-surface-subtle p-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium text-ink">Inserimento rapido ingredienti</h4>
            <Button
              type="button"
              size="sm"
              loading={parseIngredients.isPending}
              disabled={!declaration.trim()}
              onClick={() => parseIngredients.mutate(declaration.trim())}
            >
              Estrai
            </Button>
          </div>
          <Textarea
            rows={3}
            value={declaration}
            onChange={(e) => setDeclaration(e.target.value)}
            placeholder="Incolla qui la dichiarazione, es. Carni di maiale 40% (fresche 12%), Sorgo, Piselli integrali (12%), ..."
          />
          <p className="mt-1 text-xs text-ink-muted">
            Estrae nome, quantità (anche g/kg), unità, funzione e allergene in &quot;Ingredienti strutturati&quot;. Usa l&apos;AI se attiva, altrimenti il parser automatico. Rivedi le righe e salva.
          </p>
          {ingredientPreview.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-xs font-medium text-ink-muted">Lista ingredienti (derivata)</p>
              <div className="flex flex-wrap gap-1.5">
                {ingredientPreview.map((ing, i) => (
                  <span key={`${ing}-${i}`} className="rounded-full bg-surface-elevated px-2.5 py-0.5 text-xs text-ink border border-border">{ing}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Controller control={control} name="tags" render={({ field }) => <TagInput label="Tag commerciali" value={field.value} onChange={field.onChange} placeholder="es. premium, no-cereali" lowercase />} />
        </div>

        <div className="mt-5">
          <h4 className="mb-2 text-sm font-medium text-ink">Profilo nutrizionale</h4>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Input type="number" step="0.1" min={0} max={100} label="Proteine %" {...register('npProtein')} />
            <Input type="number" step="0.1" min={0} max={100} label="Grassi %" {...register('npFat')} />
            <Input type="number" step="0.1" min={0} max={100} label="Fibre %" {...register('npFiber')} />
            <Input type="number" step="0.1" min={0} max={100} label="Ceneri %" {...register('npAsh')} />
            <Input type="number" step="0.1" min={0} max={100} label="Umidità %" {...register('npMoisture')} />
            <Input type="number" step="1" min={0} label="kcal/kg" {...register('npKcal')} />
            <Select label="Fase di vita" {...register('npLifestage')}>
              <option value="">Non specificata</option>
              <option value="junior">Junior</option>
              <option value="adult">Adulto</option>
              <option value="senior">Senior</option>
              <option value="all">Tutte</option>
            </Select>
            <Select label="Livello ORME" {...register('ormeLevel')} hint="Classificazione CNO (1-5)">
              <option value="">Non specificato</option>
              <option value="1">Livello 1</option>
              <option value="2">Livello 2</option>
              <option value="3">Livello 3</option>
              <option value="4">Livello 4</option>
              <option value="5">Livello 5</option>
            </Select>
          </div>
          {isVariant && (
            <div className="mt-2 flex flex-col gap-1">
              <InheritStatus show={baseHas(base, 'nutritionalProfile')} empty={isEmptyNutrition(wAll)} preview={`Profilo nutrizionale: ${npText(base.nutritionalProfile)}`} />
              <InheritStatus show={baseHas(base, 'ormeLevel')} empty={isEmptyStr(wAll.ormeLevel)} preview={`Livello ORME ${base.ormeLevel}`} />
            </div>
          )}
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium text-ink">Ingredienti strutturati</h4>
            <Button type="button" variant="secondary" size="sm" onClick={() => appendLine({ name: '', percent: '', amount: '', unit: '', function: '', allergen: false, origin: '' })}>Aggiungi riga</Button>
          </div>
          {isVariant && <InheritStatus className="mb-2" show={baseHas(base, 'ingredientLines')} empty={isEmptyArr(liveLines)} preview={ingText(base.ingredientLines)} />}
          {lineFields.length === 0 && <p className="text-sm text-ink-muted">Nessuna riga. Dettaglia la composizione (nome, quantità, unità, funzione, allergene).</p>}
          <div className="space-y-2">
            {lineFields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-12">
                <div className="sm:col-span-3"><Input label={i === 0 ? 'Ingrediente' : undefined} placeholder="es. Pollo fresco" {...register(`ingredientLines.${i}.name`)} /></div>
                <div className="sm:col-span-2"><Input type="number" step="0.01" min={0} label={i === 0 ? 'Quantità' : undefined} {...register(`ingredientLines.${i}.amount`)} /></div>
                <div className="sm:col-span-2">
                  <Select label={i === 0 ? 'Unità' : undefined} {...register(`ingredientLines.${i}.unit`)}>
                    <option value="">-</option>
                    {INGREDIENT_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </Select>
                </div>
                <div className="sm:col-span-2"><Input label={i === 0 ? 'Funzione' : undefined} placeholder="es. fonte proteica" {...register(`ingredientLines.${i}.function`)} /></div>
                <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2"><input type="checkbox" {...register(`ingredientLines.${i}.allergen`)} /> Allergene</label>
                <div className="sm:col-span-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)}><Trash2 className="h-4 w-4" /></Button></div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <SectionTitle
          title="Componenti analitici e additivi"
          hint={isVariant
            ? 'Vuoto = eredita dal base. Incolla l\'etichetta per popolare entrambe le sezioni.'
            : 'Informazioni nutritive (componenti analitici) e additivi dichiarati in etichetta.'}
        />

        {/* Quick entry: paste the full label block (analytical constituents +
            additives) and extract both lists in one step. */}
        <div className="rounded-md border border-border bg-surface-subtle p-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium text-ink">Inserimento rapido etichetta</h4>
            <Button
              type="button"
              size="sm"
              loading={parseLabel.isPending}
              disabled={!labelText.trim()}
              onClick={() => parseLabel.mutate(labelText.trim())}
            >
              Estrai
            </Button>
          </div>
          <Textarea
            rows={4}
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            placeholder="Incolla qui componenti analitici e additivi, es. Componenti analitici: proteina grezza 33%, grassi grezzi 15%, ... Additivi nutrizionali: vitamina A 27000 UI, ... Additivi zootecnici: cloruro di ammonio 5 g."
          />
          <p className="mt-1 text-xs text-ink-muted">
            Riconosce le sezioni &quot;Componenti analitici&quot; e &quot;Additivi&quot; (anche senza intestazioni) e compila i campi di proteine/grassi/fibra/ceneri/umidità del profilo nutrizionale se vuoti. Usa l&apos;AI se attiva, altrimenti il parser automatico. Rivedi e salva.
          </p>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium text-ink">Componenti analitici</h4>
            <Button type="button" variant="secondary" size="sm" onClick={() => appendAc({ name: '', amount: '', unit: '' })}>Aggiungi riga</Button>
          </div>
          {isVariant && <InheritStatus className="mb-2" show={baseHas(base, 'analyticalConstituents')} empty={isEmptyArr(liveAc)} preview={acText(base.analyticalConstituents)} />}
          {acFields.length === 0 && <p className="text-sm text-ink-muted">Nessuna riga. Es. proteina grezza, grassi grezzi, fibra, ceneri, umidità, omega 3/6.</p>}
          <div className="space-y-2">
            {acFields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-12">
                <div className="sm:col-span-6"><Input label={i === 0 ? 'Componente' : undefined} placeholder="es. proteina grezza" {...register(`analyticalConstituents.${i}.name`)} /></div>
                <div className="sm:col-span-2"><Input type="number" step="0.01" min={0} label={i === 0 ? 'Valore' : undefined} {...register(`analyticalConstituents.${i}.amount`)} /></div>
                <div className="sm:col-span-3">
                  <Select label={i === 0 ? 'Unità' : undefined} {...register(`analyticalConstituents.${i}.unit`)}>
                    <option value="">-</option>
                    {ANALYTICAL_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </Select>
                </div>
                <div className="sm:col-span-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeAc(i)}><Trash2 className="h-4 w-4" /></Button></div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-medium text-ink">Additivi</h4>
            <Button type="button" variant="secondary" size="sm" onClick={() => appendAdd({ name: '', amount: '', unit: '', category: '' })}>Aggiungi riga</Button>
          </div>
          {isVariant && <InheritStatus className="mb-2" show={baseHas(base, 'additives')} empty={isEmptyArr(liveAdd)} preview={addText(base.additives)} />}
          {addFields.length === 0 && <p className="text-sm text-ink-muted">Nessuna riga. Vitamine, minerali, oligoelementi e additivi zootecnici.</p>}
          <div className="space-y-2">
            {addFields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-12">
                <div className="sm:col-span-4"><Input label={i === 0 ? 'Additivo' : undefined} placeholder="es. vitamina A" {...register(`additives.${i}.name`)} /></div>
                <div className="sm:col-span-2"><Input type="number" step="0.01" min={0} label={i === 0 ? 'Valore' : undefined} {...register(`additives.${i}.amount`)} /></div>
                <div className="sm:col-span-2">
                  <Select label={i === 0 ? 'Unità' : undefined} {...register(`additives.${i}.unit`)}>
                    <option value="">-</option>
                    {ADDITIVE_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <Select label={i === 0 ? 'Categoria' : undefined} {...register(`additives.${i}.category`)}>
                    <option value="">-</option>
                    {ADDITIVE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                </div>
                <div className="sm:col-span-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeAdd(i)}><Trash2 className="h-4 w-4" /></Button></div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        <SectionTitle
          title="Letture ORME"
          hint={editing ? 'Lettura della Ricetta e Scomposizione del Piatto. Genera una bozza, poi rivedila.' : 'Disponibili dopo aver salvato il prodotto.'}
          action={<Button type="button" variant="secondary" size="sm" loading={generate.isPending} onClick={onGenerate}><Sparkles className="mr-1 h-3.5 w-3.5" /> Genera bozza AI</Button>}
        />
        <div className="flex flex-col gap-5">
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Lettura della Ricetta</h4>
            {isVariant && <InheritStatus className="mb-2" show={baseHas(base, 'recipeReading')} empty={isEmptyReadingMap(recipeMap)} preview={readingText(base.recipeReading)} />}
            <StructuredReadingEditor defs={RECIPE_SECTIONS} map={recipeMap} onChange={(key, text) => setRecipeMap((prev) => ({ ...prev, [key]: text }))} />
          </div>
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Scomposizione del Piatto</h4>
            {isVariant && <InheritStatus className="mb-2" show={baseHas(base, 'plateReading')} empty={isEmptyReadingMap(plateMap)} preview={readingText(base.plateReading)} />}
            <StructuredReadingEditor defs={PLATE_SECTIONS} map={plateMap} onChange={(key, text) => setPlateMap((prev) => ({ ...prev, [key]: text }))} />
          </div>
        </div>
      </Card>

      {editing && !isVariant && (
        <Card>
          <ProductVariantsSection base={product} />
        </Card>
      )}

      {/* Fixed save bar: spans the whole content area (everything except the
          sidebar, which is 16rem wide on lg). Inner row aligns with the content. */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface-elevated/95 backdrop-blur lg:left-64">
        <div className="mx-auto flex max-w-7xl items-center justify-end gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/products')} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </div>
      </div>
    </form>
  );
}
